
import { type Ref, provide, ref, useRoute, useRouter } from "@nuxtjs/composition-api";
import { Component, Vue, Watch } from "nuxt-property-decorator";
import { type Route, type RouteMeta } from "vue-router";

import { isNewUser } from "@/schemes/auth0";
import { useCategoriesStore, useSettingsStore } from "@/store";
import { getExperimentsStore } from "@/store/experiments";
import { getGiftGuidesStore } from "@/store/gift-guides";
import debounce from "@/utils/debounce";
import { time } from "@/utils/enums/time";
import {
  EXPERIMENTS_AUTO_DISMISS_BRANCH_JOURNEY,
  EXPERIMENTS_DYNAMIC_LOADING_STATE,
  EXPERIMENTS_TIKTOK_IAB_NOTIFICATION,
  VARIANT_TREATMENT,
} from "@/utils/experiments/constants";
import { videoDebugKey } from "@/utils/injections";
import logger, { logLevel } from "@/utils/logger";
import { SESSION_DURATION } from "@/utils/sessionstorage/constants";

@Component({
  head(this: DefaultLayout) {
    const meta = this.$nuxt.context.$metaService.setSiteMeta(this.$nuxt.context);
    meta.afterNavigation = () => {
      if (this.$route?.query?.tab !== undefined) {
        return;
      }
      this.refreshBranchJourney();
    };

    return { ...meta, ...this.$nuxtI18nHead() };
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const showSearchBtn = ref(true);
    const branchBannerFailed = ref(false);

    provide("showSearchBtn", showSearchBtn);
    provide("utm_campaign", route.value.query.utm_campaign || "");
    if (route.value.query.ttoclid && !route.value.query.utm_source) {
      provide("utm_source", "tiktok_one");
      router.push({ path: route.value.path, query: { ...route.value.query, utm_source: "tiktok_one" } });
    } else {
      provide("utm_source", route.value.query.utm_source || "");
    }
    provide("utm_medium", route.value.query.utm_medium || "");
    provide("utm_term", route.value.query.utm_term || "");
    provide("utm_content", route.value.query.utm_content || "");
    provide(videoDebugKey, route.value.query.video_debug === "true");

    const settingsStore = useSettingsStore();
    const categoriesStore = useCategoriesStore();

    return { settingsStore, categoriesStore, showSearchBtn, branchBannerFailed };
  },
  errorCaptured(error, vm, info) {
    logger(logLevel.ERROR, info, {}, vm, error);
    return false;
  },
  fetchDelay: 0,
})
export default class DefaultLayout extends Vue {
  branchJourneyBannerHeight = 0;
  loading = true;
  settingsStore!: ReturnType<typeof useSettingsStore>;
  categoriesStore!: ReturnType<typeof useCategoriesStore>;
  showSearchBtn!: Ref<boolean | undefined>;
  branchBannerFailed!: boolean;
  isBranchListenerTicking = false;

  get version() {
    return `${this.$config.version}+${this.$config.gitSha}`;
  }

  get showAppBadgeBanner() {
    return (this.$nuxt.context.route.meta as RouteMeta[] | undefined)?.reduce(
      (val, meta) => meta.showAppBadgeBanner ?? val,
      true,
    );
  }

  get showNavBar() {
    return (this.$nuxt.context.route.meta as RouteMeta[] | undefined)?.reduce(
      (val, meta) => meta.showNavBar ?? val,
      true,
    );
  }

  get vuetifyAppBarHeight() {
    return this.$vuetify.application.top;
  }

  get splashScreenFlagName() {
    return EXPERIMENTS_DYNAMIC_LOADING_STATE;
  }

  get tikTokIabNotification() {
    return EXPERIMENTS_TIKTOK_IAB_NOTIFICATION;
  }

  get showSplashScreen() {
    return this.$route.name === "explore-displayName";
  }

  get isBranchAutoDismissFlagActive(): boolean {
    const experimentStore = getExperimentsStore(this.$store);
    return Boolean(experimentStore.variant(EXPERIMENTS_AUTO_DISMISS_BRANCH_JOURNEY)?.value === VARIANT_TREATMENT);
  }

  get isCartOpen() {
    if (!this.$auth.loggedIn) {
      return false;
    }
    return this.$route.query["show-carts"] === "true";
  }

  setup() {
    const settingsStore = useSettingsStore();

    return { settingsStore };
  }

  async fetch() {
    getExperimentsStore(this.$store).fetchVariant({ variantName: EXPERIMENTS_AUTO_DISMISS_BRANCH_JOURNEY });

    if (this.$auth.loggedIn && !isNewUser(this.$auth.user)) {
      try {
        await this.settingsStore.fetchSettings();
        // Prefer user's saved settings over local/derived values.
        this.$i18n.locale = this.settingsStore.settings?.locale ?? "en";
      } catch (error) {
        logger(logLevel.ERROR, "Error fetching user settings", {}, error);
      }
    }
  }

  mounted() {
    logger(logLevel.INFO, `Version: ${this.version}`);

    setTimeout(() => {
      this.loading = false;
    }, time.TWO_SECONDS);

    const observer = new MutationObserver((_mutations) => {
      this.branchJourneyBannerHeight = parseFloat(document.body.style.marginTop);
    });

    observer.observe(document.body, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ["style"],
    });

    this.setupBranchAutoDismissal();

    // Session duration
    sessionStorage.setItem(SESSION_DURATION, Date.now().toString());

    this.$i18n.onLanguageSwitched = (_newLocale, _oldLocale) => {
      // Only worry aboud refreshing if it's already been fetched.
      // Must be placed after setting $i18n so that the localeProperties oblect can update
      const giftGuidesStore = getGiftGuidesStore(this.$store);
      if (giftGuidesStore.haveCategoriesBeenFetched) {
        giftGuidesStore.fetchLocalizedCategories();
      }
    };
  }

  @debounce({ timeout: 50 })
  async refreshBranchJourney() {
    try {
      if (!this.branchBannerFailed) {
        await this.$branch?.refreshJourney();
      }
    } catch (error) {
      logger(logLevel.WARN, "error opening branch journey", {}, error);
      // prevent attempts to open the journey if it fails
      this.branchBannerFailed = true;
      this.$branch.initState = "error";
    }
  }

  trackOpenStore(store: string) {
    this.$amplitude?.sendDownloadAppEvent("footer", store);
  }

  get availableLanguages() {
    return this.$i18n?.locales
      ?.map((locale) => (typeof locale === "string" ? { code: locale, name: "-" } : locale))
      ?.reduce((acc, locale) => {
        acc[locale.code] = locale?.name;
        return acc;
      }, {} as Record<string, string>);
  }

  get currentLanguage() {
    return this.$i18n?.locale;
  }

  set currentLanguage(newValue) {
    this.$i18n.setLocale(newValue);
  }

  setupBranchAutoDismissal(): void {
    if (this.isBranchAutoDismissFlagActive) {
      document.addEventListener("scroll", this.branchAutoDismissListenerCallback);
    }
  }

  branchAutoDismissListenerCallback(_event: Event): void {
    if (!this.isBranchListenerTicking) {
      window.requestAnimationFrame(async () => {
        const isTargetRoute = Boolean(this.$route.name?.includes("explore"));
        const isTargetBanner = this.$branch.lastDisplayedBanner === "Small Top Banner";
        const isPastThreshold = window.scrollY > window.innerHeight * 0.5; // eslint-disable-line @typescript-eslint/no-magic-numbers
        if (isTargetRoute && isPastThreshold && isTargetBanner) {
          try {
            await this.$branch.dismissJourney();
          } catch (e) {
            // Error trap; just means journey was already dismissed
          }
        }

        if (this.$branch.areAllExploreJourneysDismissed()) {
          document.removeEventListener("scroll", this.branchAutoDismissListenerCallback);
        }

        this.isBranchListenerTicking = false;
      });
    }
    this.isBranchListenerTicking = true;
  }

  @Watch("currentLanguage")
  onLocaleUpdate(locale: string) {
    this.categoriesStore.getHashtags(locale);

    // Only update user settings if the user is logged in
    if (!this.$auth.loggedIn) {
      return;
    }

    // Only make changes if there is a meaningful update
    if (locale === this.settingsStore.settings?.locale) {
      return;
    }

    this.settingsStore.updateSettings({ locale });
  }

  @Watch("$route", { immediate: true })
  onRouteChange(_route: Route) {
    // Guard for SSR, but we want immediate so first page load is captured
    if (process.client) {
      this.$snowplow.trackPageView();
    }
  }

  onShowCarts(show: boolean) {
    const query = this.$route.query;
    const newQuery = { ...query };
    if (show) {
      newQuery["show-carts"] = "true";
    } else {
      delete newQuery["show-carts"];
    }
    if (query["show-carts"] !== newQuery["show-carts"]) {
      this.$router.replace({ path: this.$route.path, query: newQuery });
    }
  }
}
